import React, {useCallback, useMemo} from 'react';
import {
	RepositoryAccessKeyType,
	useRepositoryAccessKeysFindQuery,
	useRepositoryAccessKeyGenerateMutation,
	useRepositoryAccessKeyDeleteMutation,
} from '../services/repositoryAccessKeys';
import {sharedViewPath} from '../common/sharedView';
import ShareLinkDialog from './ShareLinkDialog';

const COPY_BY_TYPE: Record<RepositoryAccessKeyType, {title: string, description: string, privateHint: string}> = {
	[RepositoryAccessKeyType.Schema]: {
		title: 'Share schema',
		description: 'Share your collection schema to help others quickly set up a similar workflow. The entries in your collection will not be shared.',
		privateHint: 'This collection schema is private. Click here to generate a secret link.',
	},
	[RepositoryAccessKeyType.Read]: {
		title: 'Share',
		description: 'Anyone with the link can view this collection, its entries and its timeline in read-only mode.',
		privateHint: 'This collection is private. Click here to generate a secret link.',
	},
};

export type ShareRepositoryDialogProps = {
	open: boolean;
	onClose: () => void;
	repositoryId: string;
	repositoryTitle?: string;
	type: RepositoryAccessKeyType;
	/** Where a read link lands inside the shared view; the collection's entries list unless set. */
	sharedPath?: string;
};

const ShareRepositoryDialog = ({open, onClose, repositoryId, repositoryTitle, type, sharedPath}: ShareRepositoryDialogProps) => {
	const copy = COPY_BY_TYPE[type];
	const {data: keys, isLoading, isFetching, refetch} = useRepositoryAccessKeysFindQuery({repositoryId, type}, {
		skip: !open || !repositoryId,
	});
	const [generateKey, {isLoading: isGenerating}] = useRepositoryAccessKeyGenerateMutation();
	const [deleteKey, {isLoading: isDeleting}] = useRepositoryAccessKeyDeleteMutation();

	const shareLink = useMemo(() => {
		const secretKey = keys?.[0]?.secretKey;
		if (!secretKey || !repositoryId) {
			return '';
		}
		if (type === RepositoryAccessKeyType.Read) {
			return new URL(sharedViewPath(secretKey, sharedPath ?? `/collection/${repositoryId}`), window.location.origin).toString();
		}
		const url = new URL(`/replicate/${repositoryId}`, window.location.origin);
		url.searchParams.set('key', secretKey);
		return url.toString();
	}, [keys, repositoryId, type, sharedPath]);

	const handleGenerate = useCallback(async () => {
		await generateKey({repositoryId, type}).unwrap();
		refetch();
	}, [generateKey, repositoryId, type, refetch]);

	const handleRevoke = useCallback(async () => {
		const id = keys?.[0]?.id;
		if (!id) {
			return;
		}
		await deleteKey({shareLinkId: id}).unwrap();
		refetch();
	}, [deleteKey, keys, refetch]);

	return (
		<ShareLinkDialog
			open={open}
			onClose={onClose}
			title={`${copy.title}${repositoryTitle ? `: ${repositoryTitle}` : ''}`}
			description={copy.description}
			privateHint={copy.privateHint}
			shareLink={shareLink}
			isLoading={isLoading}
			isGenerating={isGenerating}
			isDeleting={isDeleting}
			busy={isLoading || isFetching || isGenerating || isDeleting}
			onGenerate={handleGenerate}
			onRevoke={handleRevoke}
		/>
	);
};

export default ShareRepositoryDialog;
